import { memo } from "react";
import { Box } from "@mui/material";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";

import {
  PALETTE,
  SHADOW_SM,
  FONT_MONO,
  DateRow,
  fmtDate,
  fmtDateShort,
  fmtDuration,
  fmtRelativeShort,
} from "./_shared";

const CombinedTimelineCard = ({
  unifiedTimeline,
  milestoneTransitions,
  todayLabel,
}) => {
  if (!unifiedTimeline.hasData) return null;

  return (
    <Box
      sx={{
        background: PALETTE.surface,
        border: `1px solid ${PALETTE.line}`,
        borderRadius: "10px",
        boxShadow: SHADOW_SM,

        mb: "16px",
      }}
    >
      <Box sx={{ px: "28px", py: "24px" }}>
        <Flx ac jb sx={{ alignItems: "baseline", mb: "22px" }}>
          <Txt
            component="h3"
            sx={{
              fontWeight: 700,
              fontSize: 16,
              letterSpacing: "-0.01em",
              m: 0,
              color: PALETTE.ink,
            }}
          >
            Milestone Progression
          </Txt>
          <Txt
            component="div"
            sx={{
              fontSize: 11,
              color: PALETTE.ink3,
              fontFamily: FONT_MONO,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            TODAY · {todayLabel}
          </Txt>
        </Flx>
        {/* Inset on each side so a milestone centered at 0% or 100%
          still has its label inside the panel. 30px is the practical
          minimum given the 110px max-width labels — combined with
          the panel's px:28 padding that gives ~3px clearance for a
          worst-case label. */}
        <Box sx={{ position: "relative", mx: "30px" }}>
          {/* Today vertical line — starts at the ruler and runs down
            through the stage segments. Skipping the milestone area
            avoids overlapping the "Today" label/date text at the top
            right. The Today milestone's own pin already provides the
            visual connection from label down to ruler. */}
          {unifiedTimeline.todayPct != null && (
            <Box
              sx={{
                position: "absolute",
                // Milestone area is 60px tall (height: 60 below).
                top: "60px",
                bottom: 0,
                left: `${unifiedTimeline.todayPct}%`,
                width: "2px",
                background: PALETTE.ink,
                transform: "translateX(-50%)",
                zIndex: 5,
                pointerEvents: "none",
              }}
            />
          )}

          {/* Top track: milestones (label, date, then a thin pin
            dropping down to the ruler). 60px tall is just enough for
            a two-line label, a date, a relative meta line, and the
            pin. */}
          <Box sx={{ position: "relative", height: 60 }}>
            {unifiedTimeline.milestones.map((m) => {
              const isToday = m.status === "today";
              const pinColor =
                m.status === "warn"
                  ? PALETTE.warn
                  : isToday
                    ? PALETTE.ink
                    : m.status === "done"
                      ? PALETTE.accent
                      : PALETTE.ink4;
              const dateColor =
                m.status === "warn"
                  ? PALETTE.warn
                  : isToday
                    ? PALETTE.ink
                    : m.status === "done"
                      ? PALETTE.ink
                      : PALETTE.ink2;
              const labelColor = isToday ? PALETTE.ink : PALETTE.ink3;
              return (
                <Box
                  key={m.key}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: `${m.leftPct}%`,
                    transform: "translateX(-50%)",
                    textAlign: "center",
                    width: "max-content",
                    maxWidth: 110,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Txt
                    component="div"
                    sx={{
                      fontSize: 10.5,
                      color: labelColor,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      fontWeight: isToday ? 700 : 500,
                      lineHeight: 1.15,
                    }}
                  >
                    {m.label}
                  </Txt>
                  <Txt
                    component="div"
                    sx={{
                      mt: "2px",
                      fontSize: 11.5,
                      color: dateColor,
                      fontFamily: FONT_MONO,
                      fontWeight:
                        isToday ||
                        m.status === "warn" ||
                        m.status === "done"
                          ? isToday
                            ? 700
                            : 500
                          : 400,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmtDateShort(m.date)}
                    {m.meta && (
                      <Box
                        component="span"
                        sx={{
                          ml: "6px",
                          color:
                            m.status === "warn"
                              ? PALETTE.warn
                              : PALETTE.ink3,
                          fontWeight: m.status === "warn" ? 600 : 400,
                        }}
                      >
                        · {m.meta}
                      </Box>
                    )}
                  </Txt>
                  {/* Pin: drops from the date line to the ruler. The
                    height matches the gap from here to the ruler so
                    the pin visually anchors the milestone. */}
                  <Box
                    sx={{
                      mt: "4px",
                      width: isToday ? "2px" : "1px",
                      height: 16,
                      background: pinColor,
                    }}
                  />
                </Box>
              );
            })}
          </Box>

          {/* Shared ruler — 4px bar with progress fill 0% → today. */}
          <Box
            sx={{
              position: "relative",
              height: 4,
              background: PALETTE.line2,
              borderRadius: "2px",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                height: "100%",
                width: `${unifiedTimeline.todayPct ?? 0}%`,
                background: PALETTE.accent,
                borderRadius: "2px",
              }}
            />
          </Box>

          {/* Bottom track: stage segments. Each segment occupies its
            actual time range; narrow segments hide their text but
            still render as a colored block (with a tooltip via the
            native title attribute). A CSS minWidth keeps very brief
            stages (1-2 day transitions) visible — they may overlap
            the next block by a few pixels, but that's preferable to
            collapsing into invisibility. */}
          <Box
            sx={{
              position: "relative",
              // Tall enough for two stacked lines (name + duration)
              // plus the segment's 1px border and inner gap.
              height: 38,
              mt: "8px",
            }}
          >
            {unifiedTimeline.segments.map((s) => {
              const isFuture = s.status === "future";
              const isCurrent = s.status === "current";
              const bg = isCurrent
                ? PALETTE.accent
                : isFuture
                  ? PALETTE.surface2
                  : PALETTE.accentSoft;
              const fg = isCurrent
                ? PALETTE.surface
                : isFuture
                  ? PALETTE.ink3
                  : PALETTE.accent;
              const border = isFuture
                ? `1px dashed ${PALETTE.ink4}`
                : `1px solid ${PALETTE.accent}`;
              const duration = fmtDuration(s.endMs - s.startMs);
              const startDateLabel = fmtDateShort(s.startMs);
              // Tiered content based on segment width. The stage name
              // is always shown when the block is at least 2% wide
              // (CSS truncates with ellipsis on narrower blocks); the
              // sub-line adds duration ≥4% and date · duration ≥8%.
              const showName = s.widthPct >= 2;
              const showSubLine = s.widthPct >= 4;
              const showDateInSub = s.widthPct >= 8;
              return (
                <Box
                  key={s.key}
                  title={[s.name, startDateLabel, duration]
                    .filter(Boolean)
                    .join(" · ")}
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `${s.leftPct}%`,
                    width: `${s.widthPct}%`,
                    // Floor for very brief stages so a 1-day window
                    // doesn't render as a 1px sliver. Tradeoff: ultra-
                    // narrow segments may slightly overlap their right
                    // neighbour — colored backgrounds + borders still
                    // distinguish them.
                    minWidth: 40,
                    background: bg,
                    border,
                    borderRadius: "4px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    px: "6px",
                    overflow: "hidden",
                    gap: "1px",
                  }}
                >
                  {showName && (
                    <Txt
                      component="span"
                      sx={{
                        fontSize: 10.5,
                        fontWeight: isCurrent ? 700 : 600,
                        color: fg,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                        lineHeight: 1.1,
                      }}
                    >
                      {s.name}
                    </Txt>
                  )}
                  {showSubLine && (duration || startDateLabel) && (
                    <Txt
                      component="span"
                      sx={{
                        fontSize: 9.5,
                        fontWeight: 500,
                        color: fg,
                        opacity: 0.75,
                        fontFamily: FONT_MONO,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                        lineHeight: 1.1,
                      }}
                    >
                      {showDateInSub && startDateLabel
                        ? duration
                          ? `${startDateLabel} · ${duration}`
                          : startDateLabel
                        : duration}
                    </Txt>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      <Box sx={{ pt: 2 }}>
        {milestoneTransitions.map((t, idx) => (
          <DateRow
            key={`${t.ms}-${t.updatedMilestoneStatus}`}
            isFirst={idx === 0}
            when={fmtDate(t.milestoneChangeDate)}
            whenSub={fmtRelativeShort(t.milestoneChangeDate)}
            what={`Reached "${t.updatedMilestoneStatus}"`}
            whatSub={[
              t.priorMilestoneStatus
                ? `from ${t.priorMilestoneStatus}`
                : null,
              t.updateBy || null,
            ]
              .filter(Boolean)
              .join(" · ")}
            badgeLabel="Stage"
            badgeTone="default"
            state="default"
          />
        ))}
      </Box>
    </Box>
  );
};

export default memo(CombinedTimelineCard);
